import { useState, useEffect, useCallback, useRef } from 'react';

interface UseSessionTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onTimeout: () => void;
  onWarning?: () => void;
  enabled?: boolean;
}

export function useSessionTimeout({
  timeoutMinutes = 15,
  warningMinutes = 2,
  onTimeout,
  onWarning,
  enabled = true,
}: UseSessionTimeoutOptions) {
  const [isWarningShown, setIsWarningShown] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const hasTimedOutRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const resetTimers = useCallback(() => {
    if (!enabled) return;

    clearAllTimers();
    setIsWarningShown(false);
    setRemainingSeconds(0);
    lastActivityRef.current = Date.now();
    hasTimedOutRef.current = false;

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    // Set warning timer
    if (warningMinutes > 0 && onWarning) {
      warningRef.current = setTimeout(() => {
        setIsWarningShown(true);
        setRemainingSeconds(warningMinutes * 60);
        onWarning();

        // Start countdown
        countdownRef.current = setInterval(() => {
          setRemainingSeconds((prev) => {
            if (prev <= 1) {
              if (countdownRef.current) {
                clearInterval(countdownRef.current);
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, warningMs);
    }

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      if (hasTimedOutRef.current) return;
      hasTimedOutRef.current = true;
      clearAllTimers();
      setIsWarningShown(false);
      onTimeout();
    }, timeoutMs);
  }, [enabled, timeoutMinutes, warningMinutes, onTimeout, onWarning, clearAllTimers]);

  const extendSession = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  const syncWithElapsedInactivity = useCallback(() => {
    if (!enabled) return;
    if (hasTimedOutRef.current) return;

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const elapsed = Date.now() - lastActivityRef.current;
    const remainingMs = timeoutMs - elapsed;

    if (remainingMs <= 0) {
      hasTimedOutRef.current = true;
      clearAllTimers();
      setIsWarningShown(false);
      setRemainingSeconds(0);
      onTimeout();
      return;
    }

    if (warningMinutes > 0 && onWarning) {
      const warningSeconds = warningMinutes * 60;
      if (remainingMs <= warningSeconds * 1000) {
        setIsWarningShown(true);
        setRemainingSeconds(Math.ceil(remainingMs / 1000));
        onWarning();
      }
    }
  }, [enabled, timeoutMinutes, warningMinutes, onTimeout, onWarning, clearAllTimers]);

  // Hidden tabs should not pause inactivity expiry; reconcile state on visibility return.
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncWithElapsedInactivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, syncWithElapsedInactivity]);

  // Track whether timers have been initialized
  const initializedRef = useRef(false);
  const isWarningShownRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isWarningShownRef.current = isWarningShown;
  }, [isWarningShown]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      initializedRef.current = false;
      return;
    }

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttled activity handler
    let lastEventTime = 0;
    const throttleMs = 1000; // Only process events every second

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastEventTime > throttleMs) {
        lastEventTime = now;
        // Only reset if not in warning state (user should explicitly extend)
        if (!isWarningShownRef.current) {
          resetTimers();
        }
      }
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup (only on first mount or re-enable)
    if (!initializedRef.current) {
      resetTimers();
      initializedRef.current = true;
    }

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [enabled, resetTimers, clearAllTimers]);

  return {
    isWarningShown,
    remainingSeconds,
    extendSession,
    resetTimers,
  };
}
